import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { employeeService, productService, productRevisionService, trainingSummaryService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, Plus, Pencil, Trash2, X, Save, GraduationCap } from 'lucide-react';

const LEVELS = [
  { value: 0, label: '0 - Non abilitato' },
  { value: 1, label: '1 - In affiancamento' },
  { value: 2, label: '2 - Autonomo' },
  { value: 3, label: '3 - Esperto' },
];

const LEVEL_COLORS = {
  0: 'bg-slate-100 text-slate-600',
  1: 'bg-amber-100 text-amber-700',
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-green-100 text-green-700',
};

const DEFAULT_GENERAL_SKILLS = [
  'Uso calibro', 'Uso micrometro', 'Uso alesametro', 'Controllo visivo',
  'Controllo finale', 'Sbavatura', 'Piazzamento', 'Carico/scarico macchina'
];

const STATUS_MAP = {
  attivo: { label: 'Attivo', cls: 'bg-green-100 text-green-700' },
  non_attivo: { label: 'Non attivo', cls: 'bg-red-100 text-red-700' },
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

export default function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ['admin', 'production', 'management'].includes(user?.role);

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});

  // General skills
  const [showGsModal, setShowGsModal] = useState(false);
  const [editingGs, setEditingGs] = useState(null);
  const [gsForm, setGsForm] = useState({ skill_name: '', level: 0, verification_date: '', notes: '' });

  // Product skills
  const [showPsModal, setShowPsModal] = useState(false);
  const [editingPs, setEditingPs] = useState(null);
  const [psForm, setPsForm] = useState({ product_id: '', revision_id: '', level: 0, verification_date: '', notes: '' });
  const [products, setProducts] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [training, setTraining] = useState(null);

  const loadEmployee = useCallback(async () => {
    try {
      setLoading(true);
      const res = await employeeService.getById(id);
      setEmployee(res.data);
    } catch { toast.error('Errore caricamento dipendente'); navigate('/employees'); } finally { setLoading(false); }
  }, [id, navigate]);

  const loadTraining = useCallback(async () => {
    try {
      const res = await trainingSummaryService.forEmployee(id);
      setTraining(res.data);
    } catch { /* no training data yet */ }
  }, [id]);

  useEffect(() => { loadEmployee(); loadTraining(); }, [loadEmployee, loadTraining]);
  useEffect(() => { productService.getAll().then(r => setProducts(r.data)).catch(() => {}); }, []);

  const loadRevisions = async (productId) => {
    if (!productId) { setRevisions([]); return; }
    try {
      const res = await productRevisionService.getAll({ product_id: productId });
      setRevisions(res.data);
    } catch { setRevisions([]); }
  };

  // Info editing
  const startEditInfo = () => {
    setInfoForm({
      employee_code: employee.employee_code, first_name: employee.first_name, last_name: employee.last_name,
      department: employee.department || '', role: employee.role || '', hire_date: employee.hire_date || '',
      status: employee.status, notes: employee.notes || ''
    });
    setEditingInfo(true);
  };
  const saveInfo = async () => {
    try {
      const payload = { ...infoForm };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      payload.employee_code = infoForm.employee_code;
      payload.first_name = infoForm.first_name;
      payload.last_name = infoForm.last_name;
      payload.status = infoForm.status || 'attivo';
      await employeeService.update(id, payload);
      toast.success('Dati aggiornati');
      setEditingInfo(false);
      loadEmployee();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  // General skills CRUD
  const openAddGs = () => { setEditingGs(null); setGsForm({ skill_name: '', level: 0, verification_date: '', notes: '' }); setShowGsModal(true); };
  const openEditGs = (s) => { setEditingGs(s); setGsForm({ skill_name: s.skill_name, level: s.level, verification_date: s.verification_date || '', notes: s.notes || '' }); setShowGsModal(true); };
  const saveGs = async () => {
    if (!gsForm.skill_name) { toast.error('Competenza obbligatoria'); return; }
    try {
      const payload = { ...gsForm, level: parseInt(gsForm.level) };
      if (!payload.verification_date) payload.verification_date = null;
      if (!payload.notes) payload.notes = null;
      if (editingGs) { await employeeService.updateGeneralSkill(id, editingGs.id, payload); toast.success('Competenza aggiornata'); }
      else { await employeeService.addGeneralSkill(id, payload); toast.success('Competenza aggiunta'); }
      setShowGsModal(false); loadEmployee();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const deleteGs = async (skillId) => {
    if (!window.confirm('Eliminare questa competenza?')) return;
    try { await employeeService.deleteGeneralSkill(id, skillId); toast.success('Competenza eliminata'); loadEmployee(); } catch { toast.error('Errore'); }
  };

  // Product skills CRUD
  const openAddPs = () => { setEditingPs(null); setPsForm({ product_id: '', revision_id: '', level: 0, verification_date: '', notes: '' }); setRevisions([]); setShowPsModal(true); };
  const openEditPs = (s) => {
    setEditingPs(s);
    setPsForm({ product_id: s.product_id, revision_id: s.revision_id || '', level: s.level, verification_date: s.verification_date || '', notes: s.notes || '' });
    if (s.product_id) loadRevisions(s.product_id);
    setShowPsModal(true);
  };
  const savePs = async () => {
    if (!psForm.product_id) { toast.error('Prodotto obbligatorio'); return; }
    try {
      const payload = { ...psForm, level: parseInt(psForm.level) };
      if (!payload.verification_date) payload.verification_date = null;
      if (!payload.notes) payload.notes = null;
      if (!payload.revision_id) payload.revision_id = null;
      if (editingPs) { await employeeService.updateProductSkill(id, editingPs.id, payload); toast.success('Competenza aggiornata'); }
      else { await employeeService.addProductSkill(id, payload); toast.success('Competenza aggiunta'); }
      setShowPsModal(false); loadEmployee();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const deletePs = async (skillId) => {
    if (!window.confirm('Eliminare questa competenza?')) return;
    try { await employeeService.deleteProductSkill(id, skillId); toast.success('Competenza eliminata'); loadEmployee(); } catch { toast.error('Errore'); }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Caricamento...</div>;
  if (!employee) return null;

  const usedGsNames = (employee.general_skills || []).map(s => s.skill_name);
  const suggestedGsNames = DEFAULT_GENERAL_SKILLS.filter(n => !usedGsNames.includes(n));

  return (
    <div className="space-y-6" data-testid="employee-detail-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/employees')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-btn"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="employee-detail-title">Scheda Dipendente</h1>
          <p className="text-sm text-slate-500">{employee.employee_code} - {employee.first_name} {employee.last_name}</p>
        </div>
      </div>

      {/* Section 1: Dati dipendente */}
      <div className="bg-white border border-slate-200 rounded-xl" data-testid="employee-info-section">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Dati dipendente</h2>
          {canEdit && !editingInfo && <Button size="sm" variant="outline" onClick={startEditInfo} data-testid="edit-info-btn"><Pencil className="w-4 h-4 mr-1" />Modifica</Button>}
          {editingInfo && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditingInfo(false)}>Annulla</Button>
              <Button size="sm" onClick={saveInfo} data-testid="save-info-btn"><Save className="w-4 h-4 mr-1" />Salva</Button>
            </div>
          )}
        </div>
        <div className="p-6">
          {editingInfo ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Codice dipendente</label>
                <input type="text" value={infoForm.employee_code} onChange={e => setInfoForm(f => ({ ...f, employee_code: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Nome</label>
                <input type="text" value={infoForm.first_name} onChange={e => setInfoForm(f => ({ ...f, first_name: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Cognome</label>
                <input type="text" value={infoForm.last_name} onChange={e => setInfoForm(f => ({ ...f, last_name: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Reparto</label>
                <input type="text" value={infoForm.department} onChange={e => setInfoForm(f => ({ ...f, department: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Ruolo</label>
                <input type="text" value={infoForm.role} onChange={e => setInfoForm(f => ({ ...f, role: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Data assunzione</label>
                <input type="date" value={infoForm.hire_date} onChange={e => setInfoForm(f => ({ ...f, hire_date: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Stato</label>
                <select value={infoForm.status} onChange={e => setInfoForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg">
                  <option value="attivo">Attivo</option><option value="non_attivo">Non attivo</option>
                </select></div>
              <div className="md:col-span-2 lg:col-span-1"><label className="block text-xs font-medium text-slate-500 mb-1">Note</label>
                <textarea value={infoForm.notes} onChange={e => setInfoForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6">
              <InfoField label="Codice dipendente" value={employee.employee_code} />
              <InfoField label="Nome" value={employee.first_name} />
              <InfoField label="Cognome" value={employee.last_name} />
              <InfoField label="Reparto" value={employee.department} />
              <InfoField label="Ruolo" value={employee.role} />
              <InfoField label="Data assunzione" value={fmtDate(employee.hire_date)} />
              <InfoField label="Stato" value={<span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_MAP[employee.status]?.cls || ''}`}>{STATUS_MAP[employee.status]?.label || employee.status}</span>} />
              <InfoField label="Note" value={employee.notes} />
            </div>
          )}
        </div>
      </div>

      {/* Section 2: Competenze generali */}
      <div className="bg-white border border-slate-200 rounded-xl" data-testid="general-skills-section">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Competenze generali</h2>
          {canEdit && <Button size="sm" onClick={openAddGs} data-testid="add-general-skill-btn"><Plus className="w-4 h-4 mr-1" />Aggiungi</Button>}
        </div>
        <div className="overflow-x-auto">
          {(employee.general_skills || []).length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">Nessuna competenza generale registrata</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-3">Competenza</th><th className="px-4 py-3">Livello</th><th className="px-4 py-3">Data verifica</th><th className="px-4 py-3">Note</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {(employee.general_skills || []).map(s => (
                  <tr key={s.id} className="hover:bg-slate-50" data-testid={`gs-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{s.skill_name}</td>
                    <td className="px-4 py-3"><span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${LEVEL_COLORS[s.level] || ''}`}>{LEVELS.find(l => l.value === s.level)?.label || s.level}</span></td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(s.verification_date)}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{s.notes || '-'}</td>
                    <td className="px-4 py-3">
                      {canEdit && <div className="flex gap-1 justify-end">
                        <button onClick={() => openEditGs(s)} className="p-1 text-slate-400 hover:text-blue-600 rounded"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteGs(s.id)} className="p-1 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                      </div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Section 3: Competenze specifiche prodotto */}
      <div className="bg-white border border-slate-200 rounded-xl" data-testid="product-skills-section">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Competenze specifiche prodotto</h2>
          {canEdit && <Button size="sm" onClick={openAddPs} data-testid="add-product-skill-btn"><Plus className="w-4 h-4 mr-1" />Aggiungi</Button>}
        </div>
        <div className="overflow-x-auto">
          {(employee.product_skills || []).length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">Nessuna competenza specifica prodotto registrata</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-3">Prodotto</th><th className="px-4 py-3">Revisione</th><th className="px-4 py-3">Livello</th><th className="px-4 py-3">Data verifica</th><th className="px-4 py-3">Note</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {(employee.product_skills || []).map(s => (
                  <tr key={s.id} className="hover:bg-slate-50" data-testid={`ps-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{s.product_code || s.product_id} {s.product_name ? `- ${s.product_name}` : ''}</td>
                    <td className="px-4 py-3 text-slate-600">{s.revision_code || '-'}</td>
                    <td className="px-4 py-3"><span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${LEVEL_COLORS[s.level] || ''}`}>{LEVELS.find(l => l.value === s.level)?.label || s.level}</span></td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(s.verification_date)}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{s.notes || '-'}</td>
                    <td className="px-4 py-3">
                      {canEdit && <div className="flex gap-1 justify-end">
                        <button onClick={() => openEditPs(s)} className="p-1 text-slate-400 hover:text-blue-600 rounded"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deletePs(s.id)} className="p-1 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                      </div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Section 4: Formazione ricevuta */}
      <div className="bg-white border border-slate-200 rounded-xl" data-testid="training-section">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Formazione ricevuta</h2>
          <GraduationCap className="w-5 h-5 text-slate-400" />
        </div>
        {!training || training.courses === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">Nessuna formazione registrata per questo dipendente.</div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-900">{training.courses}</p>
                <p className="text-xs text-slate-500 mt-0.5">Numero corsi</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-900">{training.total_hours}</p>
                <p className="text-xs text-slate-500 mt-0.5">Ore totali</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-900">{training.last_date ? fmtDate(training.last_date) : '-'}</p>
                <p className="text-xs text-slate-500 mt-0.5">Ultima formazione</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-3">Data</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Tema</th><th className="px-4 py-3">Formatore</th><th className="px-4 py-3 text-right">Durata ore</th><th className="px-4 py-3">Prodotto</th><th className="px-4 py-3">Esito</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {(training.entries || training.sessions || []).map(s => (
                    <tr key={s.id} className="hover:bg-slate-50" data-testid={`training-row-${s.id}`}>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(s.training_date)}</td>
                      <td className="px-4 py-3">{s.type === 'minute' ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{s.minute_code || 'Verbale'}</span> : <span className="text-xs text-slate-500">Sessione</span>}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">{s.topic}</td>
                      <td className="px-4 py-3 text-slate-600">{s.trainer}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{s.duration_hours}</td>
                      <td className="px-4 py-3 text-slate-600">{s.product_code ? `${s.product_code}` : '-'}</td>
                      <td className="px-4 py-3">
                        {s.result ? <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${s.result === 'superata' ? 'bg-green-100 text-green-700' : s.result === 'non_superata' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{s.result === 'superata' ? 'Superata' : s.result === 'non_superata' ? 'Non superata' : 'Da verificare'}</span> : <span className="text-xs text-slate-400">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* General Skill Modal */}
      {showGsModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
          <div className="flex min-h-full items-center justify-center">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" data-testid="gs-modal">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">{editingGs ? 'Modifica competenza' : 'Nuova competenza generale'}</h2>
                <button onClick={() => setShowGsModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Competenza *</label>
                  <input type="text" value={gsForm.skill_name} onChange={e => setGsForm(f => ({ ...f, skill_name: e.target.value }))} list="gs-suggestions" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="gs-form-name" />
                  <datalist id="gs-suggestions">{suggestedGsNames.map(n => <option key={n} value={n} />)}</datalist>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Livello</label>
                    <select value={gsForm.level} onChange={e => setGsForm(f => ({ ...f, level: parseInt(e.target.value) }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="gs-form-level">
                      {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Data verifica</label>
                    <input type="date" value={gsForm.verification_date} onChange={e => setGsForm(f => ({ ...f, verification_date: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="gs-form-date" /></div>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
                  <textarea value={gsForm.notes} onChange={e => setGsForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="gs-form-notes" /></div>
                <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowGsModal(false)}>Annulla</Button>
                  <Button size="sm" onClick={saveGs} data-testid="gs-save-btn">{editingGs ? 'Salva' : 'Aggiungi'}</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Skill Modal */}
      {showPsModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
          <div className="flex min-h-full items-center justify-center">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" data-testid="ps-modal">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">{editingPs ? 'Modifica competenza prodotto' : 'Nuova competenza prodotto'}</h2>
                <button onClick={() => setShowPsModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Prodotto *</label>
                  <select value={psForm.product_id} onChange={e => { setPsForm(f => ({ ...f, product_id: e.target.value, revision_id: '' })); loadRevisions(e.target.value); }}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="ps-form-product">
                    <option value="">Seleziona prodotto</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Revisione</label>
                  <select value={psForm.revision_id} onChange={e => setPsForm(f => ({ ...f, revision_id: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="ps-form-revision">
                    <option value="">Tutte le revisioni</option>
                    {revisions.map(r => <option key={r.id} value={r.id}>{r.revision_code}</option>)}
                  </select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Livello</label>
                    <select value={psForm.level} onChange={e => setPsForm(f => ({ ...f, level: parseInt(e.target.value) }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="ps-form-level">
                      {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Data verifica</label>
                    <input type="date" value={psForm.verification_date} onChange={e => setPsForm(f => ({ ...f, verification_date: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="ps-form-date" /></div>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
                  <textarea value={psForm.notes} onChange={e => setPsForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="ps-form-notes" /></div>
                <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowPsModal(false)}>Annulla</Button>
                  <Button size="sm" onClick={savePs} data-testid="ps-save-btn">{editingPs ? 'Salva' : 'Aggiungi'}</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="text-sm text-slate-900 mt-0.5">{value || '-'}</p>
    </div>
  );
}
